import React, { useState, useEffect, useRef } from 'react';
import {
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Grid,
    Tooltip,
    Autocomplete,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    ToggleButton,
    ToggleButtonGroup,
    Switch,
    FormControlLabel,
    Alert
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Close as CloseIcon,
    Backspace as BackspaceIcon,
    Call as CallIcon,
    Message as MessageIcon,
    Settings as SettingsIcon,
    Videocam as VideocamIcon,
    VideocamOff as VideocamOffIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    PhoneDisabled as PhoneDisabledIcon,
    ContentCopy as ContentCopyIcon,
    Cameraswitch as CameraswitchIcon,
    Compress as CompressIcon
} from '@mui/icons-material';
import axios from 'axios';

// Import the conversation slide-out panel component
import VideoCallSlideOutPanel from './VideoCallSlideOutPanel';

// API and Base URL constants
const API_BASE = '/api/twilio';
const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL ||
    (window.location.protocol + '//' + window.location.host);

// Twilio Video imports
import { connect, createLocalVideoTrack, createLocalAudioTrack } from 'twilio-video';

// Constants for API and base URL configuration
const getBaseUrl = () => {
    // Check if we're in development or production
    const isDevelopment = import.meta.env.DEV;

    if (isDevelopment) {
        // For local development, use ngrok URL for mobile testing
        return 'https://073e-202-142-133-81.ngrok-free.app';
    } else {
        // For production, use Vercel URL
        return window.location.origin;
    }
};

const FloatingDialer = () => {
    const [open, setOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [calling, setCalling] = useState(false);
    const [selectedAction, setSelectedAction] = useState('call'); // 'call' or 'message'
    const [callStatus, setCallStatus] = useState('idle');
    const [leads, setLeads] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);

    // Apple device video call state - tracks if user has explicitly clicked to enable video
    const [userInitiatedVideo, setUserInitiatedVideo] = useState(false);

    // Utility function to detect Apple devices
    const isAppleDevice = () => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        return isSafari || isIOS;
    };

    // Video call states
    const [isVideoCall, setIsVideoCall] = useState(false);
    const [videoRoom, setVideoRoom] = useState(null);
    const [localVideoTrack, setLocalVideoTrack] = useState(null);
    const [localAudioTrack, setLocalAudioTrack] = useState(null);
    const [remoteParticipants, setRemoteParticipants] = useState(new Map());
    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    const [isAudioEnabled, setIsAudioEnabled] = useState(true);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState(null);
    const [previewActive, setPreviewActive] = useState(false);
    const [isVideoExpanded, setIsVideoExpanded] = useState(false);

    // Video refs
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);

    // Audio device settings
    const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [selectedInputDevice, setSelectedInputDevice] = useState(null);
    const [selectedOutputDevice, setSelectedOutputDevice] = useState(null);

    // Video call invitation states
    const [videoCallInviteUrl, setVideoCallInviteUrl] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);

    // Conversation drawer state
    const [conversationDrawerOpen, setConversationDrawerOpen] = useState(false);

    // Auto-open conversation drawer when video call connects
    useEffect(() => {
        if (videoRoom && callStatus === 'connected') {
            setConversationDrawerOpen(true);
        }
    }, [videoRoom, callStatus]);

    // Auto-start video preview when video call is enabled (NOT on Apple devices)
    useEffect(() => {
        if (isVideoCall && !previewActive && !videoRoom && !isAppleDevice()) {
            // Only auto-start on non-Apple devices
            startVideoPreview();
        } else if (!isVideoCall && previewActive) {
            stopVideoPreview();
        }
    }, [isVideoCall, videoRoom]);

    // Clean up video preview when component unmounts
    useEffect(() => {
        return () => {
            stopVideoPreview();
        };
    }, []);

    useEffect(() => {
        axios.get('/api/leads')
            .then(res => setLeads(res.data))
            .catch(err => console.error('Failed to load leads', err));

        // Get available audio and video devices
        getMediaDevices();

        // Load saved device preferences from localStorage
        const savedInputDevice = localStorage.getItem('preferredAudioInputDevice');
        const savedOutputDevice = localStorage.getItem('preferredAudioOutputDevice');
        const savedVideoDevice = localStorage.getItem('preferredVideoDevice');

        if (savedInputDevice) {
            setSelectedInputDevice(savedInputDevice);
        }

        if (savedOutputDevice) {
            setSelectedOutputDevice(savedOutputDevice);
        }

        if (savedVideoDevice) {
            setSelectedVideoDevice(savedVideoDevice);
        }

        // Add event listeners for device changes
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        // Clean up event listener on component unmount
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
            // Clean up video room connection
            if (videoRoom) {
                videoRoom.disconnect();
            }
            // Clean up local tracks
            if (localVideoTrack) {
                localVideoTrack.stop();
            }
            if (localAudioTrack) {
                localAudioTrack.stop();
            }
        };
    }, []);

    // Handler for device change events
    const handleDeviceChange = () => {
        console.log('Media devices changed, refreshing device list');
        getMediaDevices();
    };

    // Function to explicitly check and request media permissions
    const checkAndRequestMediaPermissions = async () => {
        try {
            // Display message to user that we're requesting permissions
            setCallStatus('requesting-permissions');

            // Try to get permissions
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: isVideoCall
            });

            // If successful, close the stream and return true
            stream.getTracks().forEach(track => track.stop());

            setCallStatus('permissions-granted');
            console.log('Media permissions granted');
            return true;
        } catch (error) {
            console.error('Failed to get media permissions:', error);

            // Provide specific guidance based on error type
            let errorMessage = '';
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage = 'Please allow access to your camera and microphone in your browser settings. Look for the camera icon in your address bar and click it to change permissions.';
            } else if (error.name === 'NotFoundError') {
                errorMessage = 'No camera or microphone found. Please connect these devices and try again.';
            } else {
                errorMessage = `Unable to access media devices: ${error.message}`;
            }

            alert(errorMessage);
            setCallStatus('permissions-denied');
            return false;
        }
    };

    // Function to enumerate available audio input, output, and video devices
    const getMediaDevices = async () => {
        try {
            // Request permission to access media devices
            const permissionsGranted = await checkAndRequestMediaPermissions();

            if (!permissionsGranted) {
                console.warn('Media permissions were not granted. Cannot enumerate devices.');
                return;
            }

            // Get the list of media devices
            const devices = await navigator.mediaDevices.enumerateDevices();
            console.log('Raw device list:', devices);

            // Filter input devices (microphones)
            const inputs = devices.filter(device => device.kind === 'audioinput');
            setAudioInputDevices(inputs);

            // Filter output devices (speakers/headphones)
            const outputs = devices.filter(device => device.kind === 'audiooutput');
            setAudioOutputDevices(outputs);

            // Filter video devices (cameras)
            const videos = devices.filter(device => device.kind === 'videoinput');
            setVideoDevices(videos);

            console.log('Filtered devices:', {
                inputs: inputs.map(d => ({ id: d.deviceId, label: d.label })),
                outputs: outputs.map(d => ({ id: d.deviceId, label: d.label })),
                videos: videos.map(d => ({ id: d.deviceId, label: d.label }))
            });

            // Check if selected devices are still available
            if (selectedInputDevice) {
                const deviceStillExists = inputs.some(device => device.deviceId === selectedInputDevice);
                if (!deviceStillExists) {
                    console.warn('Selected input device no longer available, resetting to default');
                    localStorage.removeItem('preferredAudioInputDevice');
                    setSelectedInputDevice(inputs.length > 0 ? inputs[0].deviceId : '');
                }
            } else if (inputs.length > 0) {
                setSelectedInputDevice(inputs[0].deviceId);
            } else {
                setSelectedInputDevice('');
            }

            if (selectedOutputDevice) {
                const deviceStillExists = outputs.some(device => device.deviceId === selectedOutputDevice);
                if (!deviceStillExists) {
                    console.warn('Selected output device no longer available, resetting to default');
                    localStorage.removeItem('preferredAudioOutputDevice');
                    setSelectedOutputDevice(outputs.length > 0 ? outputs[0].deviceId : '');
                }
            } else if (outputs.length > 0) {
                setSelectedOutputDevice(outputs[0].deviceId);
            } else {
                setSelectedOutputDevice('');
            }

            if (selectedVideoDevice) {
                const deviceStillExists = videos.some(device => device.deviceId === selectedVideoDevice);
                if (!deviceStillExists) {
                    console.warn('Selected video device no longer available, resetting to default');
                    localStorage.removeItem('preferredVideoDevice');
                    setSelectedVideoDevice(videos.length > 0 ? videos[0].deviceId : '');
                }
            } else if (videos.length > 0) {
                setSelectedVideoDevice(videos[0].deviceId);
                console.log('Auto-selected first video device:', videos[0].deviceId, videos[0].label);
            } else {
                setSelectedVideoDevice('');
                console.warn('No video devices found');
            }

            console.log('Media devices loaded:', {
                inputs: inputs.length,
                outputs: outputs.length,
                videos: videos.length,
                selectedInput: selectedInputDevice,
                selectedOutput: selectedOutputDevice,
                selectedVideo: selectedVideoDevice
            });

            // Check if we have permission but no labels (incognito mode or permission issues)
            const hasUnlabeledDevices = [...inputs, ...outputs, ...videos].some(device => !device.label && device.deviceId !== 'default');
            if (hasUnlabeledDevices) {
                console.warn('Some devices have no labels. This might be due to permission issues or incognito mode.');
            }

            if (inputs.length === 0 && outputs.length === 0 && videos.length === 0) {
                alert('No media devices detected. Please check that your microphone, speakers, and camera are connected and that you have granted permission to use them.');
            }
        } catch (error) {
            console.error('Error accessing media devices:', error);

            // Provide more detailed error message based on the error type
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                alert('Permission to access camera and microphone was denied. Please check your browser settings and grant permission to use your camera and microphone. You may need to refresh the page after changing permissions.');
            } else if (error.name === 'NotFoundError') {
                alert('No camera or microphone found. Please connect these devices and try again.');
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                alert('Your camera or microphone is already in use by another application. Please close other applications that might be using these devices and try again.');
            } else {
                alert('Unable to access media devices. Please check your browser permissions and try again. Error: ' + error.message);
            }
        }
    };

    // Video preview functions
    const startVideoPreview = async (fromUserInteraction = false) => {
        try {
            if (previewActive || localVideoTrack) return;

            // Check if this is an Apple device and require user interaction
            if (isAppleDevice() && !fromUserInteraction && !userInitiatedVideo) {
                console.log('Apple device detected - video preview requires user interaction');
                return;
            }

            console.log('Starting video preview...');
            setPreviewActive(true);

            // If this was initiated by user interaction on Apple device, mark it as such
            if (isAppleDevice() && fromUserInteraction) {
                setUserInitiatedVideo(true);
            }

            const videoTrack = await createLocalVideoTrack({
                deviceId: selectedVideoDevice || undefined,
                width: 640,
                height: 480
            });

            setLocalVideoTrack(videoTrack);

            // Attach video track to preview element
            if (localVideoRef.current && videoTrack) {
                // Use React-friendly DOM manipulation
                // Clear any existing video elements first by checking for them
                const existingVideos = localVideoRef.current.querySelectorAll('video');
                existingVideos.forEach(video => {
                    if (video.srcObject) {
                        const tracks = video.srcObject.getTracks();
                        tracks.forEach(track => track.stop());
                    }
                    video.remove();
                });

                const videoElement = videoTrack.attach();
                videoElement.style.width = '100%';
                videoElement.style.height = '100%';
                videoElement.style.objectFit = 'cover';
                videoElement.style.borderRadius = '8px';
                localVideoRef.current.appendChild(videoElement);
                console.log('Video preview started successfully');
            }
        } catch (error) {
            console.error('Error starting video preview:', error);
            setPreviewActive(false);

            // Show user-friendly error message
            let errorMessage = 'Unable to start video preview: ';
            if (error.name === 'NotAllowedError') {
                errorMessage += 'Camera permission denied. Please allow camera access.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += 'No camera found. Please connect a camera.';
            } else if (error.name === 'NotReadableError') {
                errorMessage += 'Camera is in use by another application.';
            } else {
                errorMessage += error.message;
            }

            alert(errorMessage);
        }
    };

    const stopVideoPreview = () => {
        if (localVideoTrack && previewActive) {
            console.log('Stopping video preview...');
            localVideoTrack.stop();
            setLocalVideoTrack(null);
            setPreviewActive(false);

            // Clear the video element using React-friendly approach
            if (localVideoRef.current) {
                // Only remove video elements, not all content
                const existingVideos = localVideoRef.current.querySelectorAll('video');
                existingVideos.forEach(video => {
                    if (video.srcObject) {
                        const tracks = video.srcObject.getTracks();
                        tracks.forEach(track => track.stop());
                    }
                    video.remove();
                });
            }
        }
    };

    // Camera switching functionality
    const switchCamera = async () => {
        if (!videoDevices || videoDevices.length <= 1) {
            console.log('No additional cameras available to switch to');
            return;
        }

        try {
            // Find current camera index
            const currentIndex = videoDevices.findIndex(device => device.deviceId === selectedVideoDevice);

            // Get next camera (cycle through available cameras)
            const nextIndex = (currentIndex + 1) % videoDevices.length;
            const nextDevice = videoDevices[nextIndex];

            console.log(`Switching camera from ${selectedVideoDevice} to ${nextDevice.deviceId}`);

            // Update selected video device
            setSelectedVideoDevice(nextDevice.deviceId);
            localStorage.setItem('preferredVideoDevice', nextDevice.deviceId);

            // If we have an active video track, replace it
            if (localVideoTrack) {
                const oldTrack = localVideoTrack;

                try {
                    // Create new video track with the new camera
                    const newVideoTrack = await createLocalVideoTrack({
                        deviceId: nextDevice.deviceId,
                        width: 640,
                        height: 480
                    });

                    // Update local video track
                    setLocalVideoTrack(newVideoTrack);

                    // If we're in a video call, replace the track in the room
                    if (videoRoom) {
                        // Find the local video track publication
                        const localParticipant = videoRoom.localParticipant;
                        const videoPublication = Array.from(localParticipant.videoTracks.values())
                            .find(pub => pub.track === oldTrack);

                        if (videoPublication) {
                            // Unpublish old track and publish new one
                            await localParticipant.unpublishTrack(oldTrack);
                            await localParticipant.publishTrack(newVideoTrack);
                            console.log('Successfully switched camera in video call');
                        }
                    }

                    // Update video preview if active
                    if (localVideoRef.current) {
                        // Clear existing videos using React-friendly approach
                        const existingVideos = localVideoRef.current.querySelectorAll('video');
                        existingVideos.forEach(video => {
                            if (video.srcObject) {
                                const tracks = video.srcObject.getTracks();
                                tracks.forEach(track => track.stop());
                            }
                            video.remove();
                        });

                        const videoElement = newVideoTrack.attach();
                        videoElement.style.width = '100%';
                        videoElement.style.height = '100%';
                        videoElement.style.objectFit = 'cover';
                        videoElement.style.borderRadius = '8px';
                        localVideoRef.current.appendChild(videoElement);
                    }

                    // Stop the old track
                    oldTrack.stop();

                } catch (error) {
                    console.error('Error switching camera:', error);
                    // Revert the device selection on error
                    const originalDevice = videoDevices[currentIndex];
                    if (originalDevice) {
                        setSelectedVideoDevice(originalDevice.deviceId);
                        localStorage.setItem('preferredVideoDevice', originalDevice.deviceId);
                    }

                    // Show user-friendly error message
                    alert(`Failed to switch camera: ${error.message}`);
                }
            }

        } catch (error) {
            console.error('Error in camera switching:', error);
            alert(`Camera switching failed: ${error.message}`);
        }
    };

    // Video calling functions
    const createLocalTracks = async () => {
        try {
            const audioTrack = await createLocalAudioTrack({
                deviceId: selectedInputDevice || undefined
            });

            // Reuse existing video track if available, otherwise create new one
            let videoTrack = null;
            if (isVideoCall) {
                if (localVideoTrack && previewActive) {
                    // Reuse existing preview track
                    videoTrack = localVideoTrack;
                    console.log('Reusing existing video track from preview');
                } else {
                    // Create new video track
                    videoTrack = await createLocalVideoTrack({
                        deviceId: selectedVideoDevice || undefined,
                        width: 640,
                        height: 480
                    });
                    console.log('Created new video track for call');
                }
            }

            setLocalAudioTrack(audioTrack);
            if (videoTrack && !localVideoTrack) {
                setLocalVideoTrack(videoTrack);
            }

            return { audioTrack, videoTrack };
        } catch (error) {
            console.error('Error creating local tracks:', error);
            throw error;
        }
    }; const sendVideoCallInvitation = async (phoneNumber, roomName, accessToken) => {
        try {
            // Generate a separate token for the recipient with a different identity
            const recipientTokenResponse = await axios.post(`${API_BASE}/video-token`, {
                identity: `recipient_${Date.now()}`,
                room: roomName
            });

            const { accessToken: recipientToken } = recipientTokenResponse.data;

            // Create a shareable URL for the video call that opens the video call page directly
            // We need to ensure this matches the server-side URL and is accessible from other devices
            let inviteUrl = `${APP_BASE_URL}/video-call.html?room=${encodeURIComponent(roomName)}&token=${encodeURIComponent(recipientToken)}`;

            // Ensure we're using HTTPS for camera permissions in Safari
            if (!inviteUrl.startsWith('https://')) {
                // Convert to https if not already
                inviteUrl = inviteUrl.replace('http://', 'https://');
                console.warn('Converted invite URL to HTTPS for better camera permission support:', inviteUrl);
            }

            // Log the full invite URL for debugging
            console.log('Created video invite URL:', inviteUrl);
            console.log('Using base URL:', APP_BASE_URL);

            // Check if we're using localhost, which won't work for cross-device testing
            if (APP_BASE_URL.includes('localhost')) {
                console.warn('WARNING: Using localhost for video call URL. This will not work on other devices!');
                console.warn('Set VITE_APP_BASE_URL to your server IP in .env file');
            }

            // Send SMS invitation
            const response = await axios.post(`${API_BASE}/video-invite`, {
                to: phoneNumber,
                roomName: roomName,
                inviteUrl: inviteUrl
            });

            setVideoCallInviteUrl(inviteUrl);
            setShowInviteModal(true);

            console.log('Video call invitation sent:', response.data);
        } catch (error) {
            console.error('Error sending video call invitation:', error);
        }
    };

    const connectToVideoRoom = async (roomName, accessToken) => {
        try {
            const { audioTrack, videoTrack } = await createLocalTracks();

            const tracks = [audioTrack];
            if (videoTrack) {
                tracks.push(videoTrack);
            }

            const room = await connect(accessToken, {
                name: roomName,
                tracks,
                video: isVideoCall,
                audio: true
            });

            setVideoRoom(room);
            setCallStatus('connected');

            // Handle participant events
            room.participants.forEach(participant => {
                handleParticipantConnected(participant);
            });

            room.on('participantConnected', handleParticipantConnected);
            room.on('participantDisconnected', handleParticipantDisconnected);
            room.on('disconnected', handleRoomDisconnected);

            console.log('Connected to video room:', roomName);
            return room;
        } catch (error) {
            console.error('Error connecting to video room:', error);
            setCallStatus('failed');
            throw error;
        }
    };

    const handleParticipantConnected = (participant) => {
        console.log('Participant connected:', participant.identity);

        const participantDiv = document.createElement('div');
        participantDiv.id = participant.sid;
        participantDiv.style.display = 'flex';
        participantDiv.style.flexDirection = 'column';
        participantDiv.style.margin = '10px';

        if (remoteVideoRef.current) {
            remoteVideoRef.current.appendChild(participantDiv);
        }

        participant.tracks.forEach(publication => {
            if (publication.isSubscribed) {
                const track = publication.track;
                participantDiv.appendChild(track.attach());
            }
        });

        participant.on('trackSubscribed', track => {
            participantDiv.appendChild(track.attach());
        });

        participant.on('trackUnsubscribed', track => {
            track.detach().forEach(element => element.remove());
        });

        setRemoteParticipants(prev => new Map(prev.set(participant.sid, participant)));
    };

    const handleParticipantDisconnected = (participant) => {
        console.log('Participant disconnected:', participant.identity);
        const participantDiv = document.getElementById(participant.sid);
        if (participantDiv) {
            participantDiv.remove();
        }
        setRemoteParticipants(prev => {
            const newMap = new Map(prev);
            newMap.delete(participant.sid);
            return newMap;
        });
    };

    const handleRoomDisconnected = (room, error) => {
        console.log('Disconnected from room:', room.name, error ? 'Error:' : '', error);
        setVideoRoom(null);
        setCallStatus('idle');

        // Clean up local tracks
        if (localVideoTrack) {
            localVideoTrack.stop();
            setLocalVideoTrack(null);
        }
        if (localAudioTrack) {
            localAudioTrack.stop();
            setLocalAudioTrack(null);
        }

        // Clear remote participants
        setRemoteParticipants(new Map());

        // Clear video elements
        if (localVideoRef.current) {
            localVideoRef.current.innerHTML = '';
        }
        if (remoteVideoRef.current) {
            remoteVideoRef.current.innerHTML = '';
        }

        // Close conversation drawer when video call disconnects
        setConversationDrawerOpen(false);
    };

    const toggleVideo = () => {
        if (localVideoTrack) {
            if (isVideoEnabled) {
                localVideoTrack.disable();
            } else {
                localVideoTrack.enable();
            }
            setIsVideoEnabled(!isVideoEnabled);
        }
    };

    const toggleAudio = () => {
        if (localAudioTrack) {
            if (isAudioEnabled) {
                localAudioTrack.disable();
            } else {
                localAudioTrack.enable();
            }
            setIsAudioEnabled(!isAudioEnabled);
        }
    };

    const disconnectFromRoom = () => {
        if (videoRoom) {
            videoRoom.disconnect();
        }
    };

    const toggleExpandedVideo = () => {
        setIsVideoExpanded(!isVideoExpanded);
    };

    // Apple device video enablement function
    const handleEnableVideoForApple = async () => {
        try {
            console.log('User clicked to enable video on Apple device');
            setUserInitiatedVideo(true);

            // Request media permissions with explicit user interaction
            await checkAndRequestMediaPermissions();

            // Start video preview now that user has clicked
            await startVideoPreview(true);

            console.log('Video enabled for Apple device via user interaction');
        } catch (error) {
            console.error('Failed to enable video for Apple device:', error);

            // Show user-friendly error message
            let errorMessage = 'Unable to enable video: ';
            if (error.name === 'NotAllowedError') {
                errorMessage += 'Camera permission denied. Please allow camera access in your browser settings.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += 'No camera found. Please connect a camera and try again.';
            } else {
                errorMessage += error.message;
            }

            alert(errorMessage);
            setUserInitiatedVideo(false); // Reset state on error
        }
    };

    // Function to save audio and video device preferences
    const saveAudioSettings = () => {
        try {
            // Save to localStorage
            if (selectedInputDevice) {
                localStorage.setItem('preferredAudioInputDevice', selectedInputDevice);
                console.log('Saved input device:', selectedInputDevice);
            } else {
                localStorage.removeItem('preferredAudioInputDevice');
            }

            if (selectedOutputDevice) {
                localStorage.setItem('preferredAudioOutputDevice', selectedOutputDevice);
                console.log('Saved output device:', selectedOutputDevice);
            } else {
                localStorage.removeItem('preferredAudioOutputDevice');
            }

            if (selectedVideoDevice) {
                localStorage.setItem('preferredVideoDevice', selectedVideoDevice);
                console.log('Saved video device:', selectedVideoDevice);
            } else {
                localStorage.removeItem('preferredVideoDevice');
            }

            // Show feedback
            const inputName = audioInputDevices.find(d => d.deviceId === selectedInputDevice)?.label || 'Default microphone';
            const outputName = audioOutputDevices.find(d => d.deviceId === selectedOutputDevice)?.label || 'Default speaker';
            const videoName = videoDevices.find(d => d.deviceId === selectedVideoDevice)?.label || 'Default camera';

            alert(`Media settings saved!\n\nMicrophone: ${inputName}\nSpeaker: ${outputName}\nCamera: ${videoName}`);

            // Close the settings dialog
            setAudioSettingsOpen(false);
        } catch (error) {
            console.error('Error saving media settings:', error);
            alert('Failed to save audio settings. Please try again.');
        }
    };

    // Test audio device function
    const testAudioDevice = async (deviceType) => {
        try {
            if (deviceType === 'input' && selectedInputDevice) {
                // Test microphone
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: { deviceId: { exact: selectedInputDevice } }
                });

                // Create audio context to visualize sound
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                const analyser = audioContext.createAnalyser();
                const microphone = audioContext.createMediaStreamSource(stream);

                microphone.connect(analyser);

                // Show a temporary "Microphone active" message
                alert('Microphone test active. Speak to test your microphone.');

                // Stop microphone test after 5 seconds
                setTimeout(() => {
                    stream.getTracks().forEach(track => track.stop());
                    alert('Microphone test completed.');
                }, 5000);
            } else if (deviceType === 'output' && selectedOutputDevice) {
                // Test speaker with a simple beep sound
                const audio = new Audio();

                // If browser supports setSinkId (Chrome), use it
                if (typeof audio.setSinkId === 'function') {
                    await audio.setSinkId(selectedOutputDevice);
                }

                // Play a simple beep sound
                audio.src = '/audio/beep.mp3'; // Make sure to add this file to your public folder
                audio.play();
                alert('Playing test sound...');
            } else if (deviceType === 'video' && selectedVideoDevice) {
                console.log('Testing camera device:', selectedVideoDevice);

                // Add timeout wrapper for camera access
                const cameraTimeout = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('Camera timeout - device took too long to respond')), 10000);
                });

                const getCameraStream = async () => {
                    try {
                        // Try with more permissive constraints first
                        const constraints = {
                            video: {
                                deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
                                width: { ideal: 640, max: 1280 },
                                height: { ideal: 480, max: 720 },
                                frameRate: { ideal: 15, max: 30 }
                            }
                        };

                        console.log('Requesting camera with constraints:', constraints);
                        return await navigator.mediaDevices.getUserMedia(constraints);
                    } catch (error) {
                        console.warn('Failed with exact device ID, trying without:', error);
                        // Fallback: try without exact device ID
                        return await navigator.mediaDevices.getUserMedia({
                            video: {
                                width: { ideal: 640, max: 1280 },
                                height: { ideal: 480, max: 720 },
                                frameRate: { ideal: 15, max: 30 }
                            }
                        });
                    }
                };

                const stream = await Promise.race([getCameraStream(), cameraTimeout]);
                console.log('Camera stream acquired successfully');

                // Create a temporary video element to show camera feed
                const video = document.createElement('video');
                video.srcObject = stream;
                video.autoplay = true;
                video.muted = true; // Important for autoplay in some browsers
                video.playsInline = true; // Important for mobile
                video.style.width = '200px';
                video.style.height = '150px';
                video.style.border = '2px solid #2196f3';
                video.style.borderRadius = '8px';

                // Show video in a temporary modal
                const modal = document.createElement('div');
                modal.style.position = 'fixed';
                modal.style.top = '50%';
                modal.style.left = '50%';
                modal.style.transform = 'translate(-50%, -50%)';
                modal.style.background = 'white';
                modal.style.padding = '20px';
                modal.style.borderRadius = '10px';
                modal.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
                modal.style.zIndex = '10000';
                modal.style.textAlign = 'center';

                const title = document.createElement('h3');
                title.textContent = 'Camera Test';
                title.style.margin = '0 0 10px 0';

                const closeBtn = document.createElement('button');
                closeBtn.textContent = 'Close';
                closeBtn.style.marginTop = '10px';
                closeBtn.style.padding = '8px 16px';
                closeBtn.style.background = '#2196f3';
                closeBtn.style.color = 'white';
                closeBtn.style.border = 'none';
                closeBtn.style.borderRadius = '4px';
                closeBtn.style.cursor = 'pointer';

                modal.appendChild(title);
                modal.appendChild(video);
                modal.appendChild(closeBtn);
                document.body.appendChild(modal);

                // Wait for video to load before showing modal
                video.onloadedmetadata = () => {
                    console.log('Video metadata loaded, camera is working');
                };

                closeBtn.onclick = () => {
                    console.log('Stopping camera test');
                    stream.getTracks().forEach(track => {
                        track.stop();
                        console.log('Stopped track:', track.kind, track.label);
                    });
                    if (document.body.contains(modal)) {
                        document.body.removeChild(modal);
                    }
                };

                // Auto close after 10 seconds
                setTimeout(() => {
                    if (document.body.contains(modal)) {
                        closeBtn.click();
                    }
                }, 10000);
            }
        } catch (error) {
            console.error(`Error testing ${deviceType} device:`, error);

            let errorMessage = `Failed to test ${deviceType} device: ${error.message}`;

            if (deviceType === 'video') {
                if (error.message.includes('timeout')) {
                    errorMessage = 'Camera timeout - the camera device is taking too long to respond. This could be because:\n\n• The camera is being used by another application\n• The camera driver needs to be updated\n• The camera hardware is not responding\n\nTry:\n• Closing other apps that might be using the camera\n• Unplugging and reconnecting the camera\n• Restarting your browser\n• Selecting a different camera if available';
                } else if (error.name === 'NotAllowedError') {
                    errorMessage = 'Camera access denied. Please allow camera permissions in your browser and try again.';
                } else if (error.name === 'NotFoundError') {
                    errorMessage = 'Camera not found. Please check that your camera is connected and not being used by another application.';
                } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                    errorMessage = 'Camera is already in use by another application. Please close other apps using the camera and try again.';
                } else if (error.name === 'OverconstrainedError') {
                    errorMessage = 'Camera does not support the requested settings. Try selecting a different camera.';
                }
            }

            alert(errorMessage);
        }
    };

    // Replace dialpadNumbers with sublabels for iOS style
    const dialpad = [
        [
            { main: '1', sub: '' },
            { main: '2', sub: 'ABC' },
            { main: '3', sub: 'DEF' }
        ],
        [
            { main: '4', sub: 'GHI' },
            { main: '5', sub: 'JKL' },
            { main: '6', sub: 'MNO' }
        ],
        [
            { main: '7', sub: 'PQRS' },
            { main: '8', sub: 'TUV' },
            { main: '9', sub: 'WXYZ' }
        ],
        [
            { main: '*', sub: '' },
            { main: '0', sub: '+' },
            { main: '#', sub: '' }
        ]
    ];

    const handleDialpadClick = (digit) => {
        if (digit === '+') {
            // Only allow + at the beginning of the number
            if (phoneNumber === '') {
                setPhoneNumber('+');
            }
        } else {
            // Add the digit to the phone number
            setPhoneNumber(prev => {
                // Make sure to update state directly without going through formatPhoneNumber
                return prev + digit;
            });
        }
    };

    const handleBackspace = () => {
        setPhoneNumber(prev => prev.slice(0, -1));
    };

    const handleCall = async () => {
        if (!phoneNumber.trim()) return;

        // Check for Apple device video call restrictions
        if (isVideoCall && isAppleDevice() && !userInitiatedVideo) {
            alert('Please click "Enable Video for Call" button first to enable camera permissions on Safari.');
            return;
        }

        setCallStatus('dialing');
        console.log('Attempting to call:', phoneNumber);

        try {
            setCalling(true);
            setCallStatus('ringing');
            const fromNumber = '+61489263516'; // your Twilio number

            // Normalize to Australian format: drop leading 0 and prefix +61
            let toNumber = phoneNumber;
            if (toNumber.startsWith('0')) {
                toNumber = '+61' + toNumber.slice(1);
            } else if (!toNumber.startsWith('+')) {
                toNumber = '+61' + toNumber;
            }
            console.log('Calling normalized number:', toNumber);

            if (isVideoCall) {
                // Handle video call
                try {
                    const roomName = `call_${toNumber.replace(/\D/g, '')}_${Date.now()}`;

                    // Request video access token for the caller
                    const callerTokenResponse = await axios.post(`${API_BASE}/video-token`, {
                        identity: `caller_${Date.now()}`,
                        room: roomName
                    });

                    const { accessToken: callerToken } = callerTokenResponse.data;

                    // Request a separate video access token for the recipient
                    const recipientTokenResponse = await axios.post(`${API_BASE}/video-token`, {
                        identity: `recipient_${Date.now()}`,
                        room: roomName
                    });

                    const { accessToken: recipientToken } = recipientTokenResponse.data;

                    // Send invitation to the other person with their own token
                    await sendVideoCallInvitation(toNumber, roomName, recipientToken);

                    // Connect to video room with caller token
                    await connectToVideoRoom(roomName, callerToken);

                    setCallStatus('connected');
                    alert('Video call initiated successfully! Invitation sent to the other person.');
                } catch (error) {
                    console.error('Video call failed:', error);
                    setCallStatus('failed');
                    alert(`Video call failed: ${error.response?.data?.error || error.message}`);
                }
            } else {
                // Handle regular voice call
                const response = await axios.post(`${API_BASE}/calls`, {
                    to: toNumber,
                    from: fromNumber,
                    audioInput: selectedInputDevice,
                    audioOutput: selectedOutputDevice
                });

                console.log('Call response:', response.data);
                setCallStatus('connected');
                alert('Call initiated successfully!');

                // Only close the dialer for regular voice calls
                setOpen(false);
                setPhoneNumber('');
            }
        } catch (error) {
            console.error('Error making call:', error);
            const status = error.response?.data?.status || 'failed';
            setCallStatus(status);
            alert(`Call failed: ${error.response?.data?.error || error.message}`);
        } finally {
            setCalling(false);
        }
    };

    const handleMessage = async () => {
        if (!phoneNumber.trim()) return;

        try {
            // Normalize to Australian format for messaging
            let msgNumber = phoneNumber;
            if (msgNumber.startsWith('0')) {
                msgNumber = '+61' + msgNumber.slice(1);
            } else if (!msgNumber.startsWith('+')) {
                msgNumber = '+61' + msgNumber;
            }

            // Create a new conversation
            const response = await axios.post(`${API_BASE}/conversations`, {
                phoneNumber: msgNumber,
                friendlyName: `Chat with ${phoneNumber}`
            });

            // Close dialer and potentially open conversations
            setOpen(false);
            setPhoneNumber('');

            // You might want to emit an event here to open the conversations component
            console.log('Created conversation:', response.data);
        } catch (error) {
            console.error('Error creating conversation:', error);
        }
    };

    const formatPhoneNumber = (number) => {
        // Handle international numbers that start with +
        if (number.startsWith('+')) {
            return number; // Keep international numbers as-is for now
        }

        // Simple phone number formatting for US numbers
        const cleaned = number.replace(/\D/g, '');
        if (cleaned.length >= 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        }
        return number;
    };

    return (
        <>
            {/* Floating Action Button */}
            <Fab
                color="primary"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000,
                    backgroundColor: '#2196f3',
                    '&:hover': {
                        backgroundColor: '#1976d2'
                    }
                }}
                onClick={() => setOpen(true)}
            >
                <PhoneIcon />
            </Fab>

            {/* Dialer Dialog */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth={conversationDrawerOpen ? "lg" : "xs"}
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#1a1a1a',
                        color: '#ffffff',
                        minHeight: '600px',
                        width: conversationDrawerOpen ? '800px' : 'auto',
                        maxWidth: conversationDrawerOpen ? '800px' : 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease-in-out'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#ffffff' }}>
                    Connect Dialer
                    <Box>
                        <IconButton
                            onClick={() => setAudioSettingsOpen(true)}
                            sx={{ color: '#ffffff', mr: 1 }}
                        >
                            <SettingsIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => setOpen(false)}
                            sx={{ color: '#ffffff' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {/* Lead Search */}
                    <Autocomplete
                        options={leads}
                        getOptionLabel={lead => `${lead.customer} (${lead.phone}) #${lead.trak_client_id}`}
                        renderOption={(props, lead) => (
                            <li {...props} key={lead.id}>
                                {lead.customer} — {lead.phone} — #{lead.trak_client_id}
                            </li>
                        )}
                        value={selectedLead}
                        onChange={(e, val) => {
                            setSelectedLead(val);
                            setPhoneNumber(val?.phone || '');
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Search Leads"
                                variant="outlined"
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: '#111',
                                        background: '#f5f5f5',
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: '#b0b0b0' }
                                    }
                                }}
                                InputLabelProps={{ style: { color: '#888' } }}
                            />
                        )}
                    />
                    {/* Phone Number Display */}
                    <Box sx={{ mb: 2, mt: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <TextField
                            fullWidth
                            value={formatPhoneNumber(phoneNumber)}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.startsWith('+')) {
                                    // Allow + only at the beginning followed by digits
                                    setPhoneNumber(value.replace(/[^\d+]/g, '').replace(/^\+?/, '+'));
                                } else {
                                    // Only allow digits for non-international numbers
                                    setPhoneNumber(value.replace(/\D/g, ''));
                                }
                            }}
                            placeholder=""
                            variant="standard"
                            autoComplete="tel"
                            type="text"
                            InputProps={{
                                disableUnderline: true,
                                inputMode: "tel"
                            }}
                            inputProps={{
                                style: {
                                    fontSize: '2.2rem',
                                    textAlign: 'center',
                                    letterSpacing: '2px',
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    background: 'transparent',
                                    border: 'none',
                                    outline: 'none',
                                    padding: '8px 4px'
                                },
                                pattern: "[0-9+]*"
                            }}
                            sx={{
                                mb: 1,
                                width: '80%',
                                background: 'transparent',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '12px',
                                '& .MuiInputBase-root': {
                                    justifyContent: 'center',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#ffffff',
                                    boxShadow: 'none',
                                    fontWeight: 500
                                },
                                '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                                    borderBottom: 'none'
                                }
                            }}
                        />
                        {/* Enhanced Status Display */}
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {callStatus === 'requesting-permissions' && (
                                <Alert severity="info" sx={{ mb: 1, width: '100%' }}>
                                    Please allow access to your camera and microphone when prompted.
                                </Alert>
                            )}

                            {callStatus === 'permissions-denied' && (
                                <Alert severity="error" sx={{ mb: 1, width: '100%' }}>
                                    Unable to access media devices. Please check your browser permissions.
                                    <Button
                                        size="small"
                                        sx={{ mt: 1 }}
                                        onClick={checkAndRequestMediaPermissions}
                                    >
                                        Try Again
                                    </Button>
                                </Alert>
                            )}

                            <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
                                Status: {callStatus === 'permissions-denied' ? 'Permission Denied' :
                                    callStatus === 'requesting-permissions' ? 'Requesting Permissions' :
                                        callStatus === 'permissions-granted' ? 'Permissions Granted' : callStatus}
                            </Typography>

                            {/* Device Status Indicators */}
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 1,
                                alignItems: 'center',
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                borderRadius: '4px',
                                padding: '4px 8px'
                            }}>
                                <Tooltip title={selectedInputDevice ? "Microphone connected" : "No microphone"}>
                                    <Box sx={{
                                        color: selectedInputDevice ? '#4caf50' : '#f44336',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {selectedInputDevice ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
                                    </Box>
                                </Tooltip>

                                <Tooltip title={selectedOutputDevice ? "Speakers connected" : "No speakers"}>
                                    <Box sx={{
                                        color: selectedOutputDevice ? '#4caf50' : '#f44336',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <span role="img" aria-label="speaker">🔊</span>
                                    </Box>
                                </Tooltip>

                                <Tooltip title={selectedVideoDevice ? "Camera connected" : "No camera"}>
                                    <Box sx={{
                                        color: selectedVideoDevice ? '#4caf50' : '#f44336',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {selectedVideoDevice ? <VideocamIcon fontSize="small" /> : <VideocamOffIcon fontSize="small" />}
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>

                    {/* Video Call Toggle */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isVideoCall}
                                    onChange={(e) => {
                                        const newVideoCallState = e.target.checked;
                                        setIsVideoCall(newVideoCallState);

                                        // Reset Apple device video state when video call is turned off
                                        if (!newVideoCallState && isAppleDevice()) {
                                            setUserInitiatedVideo(false);
                                        }
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#2196f3',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#2196f3',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isVideoCall ? <VideocamIcon /> : <PhoneIcon />}
                                    {isVideoCall ? 'Video Call' : 'Voice Call'}
                                </Typography>
                            }
                        />
                    </Box>

                    {/* Apple Device Video Enablement */}
                    {isVideoCall && isAppleDevice() && !userInitiatedVideo && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', color: '#ffffff' }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Safari requires user interaction to enable video calling.
                                </Typography>
                                <Typography variant="caption">
                                    Click the button below to enable your camera and microphone.
                                </Typography>
                            </Alert>
                            <Button
                                variant="contained"
                                onClick={() => handleEnableVideoForApple()}
                                sx={{
                                    backgroundColor: '#ff9800',
                                    color: '#000',
                                    '&:hover': {
                                        backgroundColor: '#f57c00',
                                    },
                                    mb: 2
                                }}
                                startIcon={<VideocamIcon />}
                            >
                                Enable Video for Call
                            </Button>
                        </Box>
                    )}

                    {/* Test button for slide-out panel */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setConversationDrawerOpen(!conversationDrawerOpen)}
                            sx={{
                                color: '#ffffff',
                                borderColor: '#2196f3',
                                '&:hover': {
                                    borderColor: '#1976d2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)'
                                }
                            }}
                        >
                            {conversationDrawerOpen ? 'Close Chat Panel' : 'Open Chat Panel'}
                        </Button>
                    </Box>

                    {/* Video Preview (shown when video call is enabled) */}
                    {isVideoCall && !isVideoExpanded && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 1 }}>
                                Video Preview
                            </Typography>
                            <Box
                                ref={localVideoRef}
                                sx={{
                                    width: '200px',
                                    height: '150px',
                                    backgroundColor: '#333',
                                    borderRadius: '8px',
                                    margin: '0 auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #2196f3',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    '& video': {
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '6px'
                                    }
                                }}
                            >
                                {!localVideoTrack && (
                                    <Typography sx={{ color: '#888', textAlign: 'center', p: 1 }}>
                                        {isAppleDevice() && !userInitiatedVideo
                                            ? 'Click "Enable Video" above to start camera'
                                            : 'Camera will start during call'
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Video Controls (shown during video call) */}
                    {videoRoom && isVideoCall && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                            <IconButton
                                onClick={toggleVideo}
                                sx={{
                                    backgroundColor: isVideoEnabled ? '#4caf50' : '#f44336',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: isVideoEnabled ? '#43a047' : '#e53935',
                                    }
                                }}
                            >
                                {isVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                            </IconButton>
                            <IconButton
                                onClick={toggleAudio}
                                sx={{
                                    backgroundColor: isAudioEnabled ? '#4caf50' : '#f44336',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: isAudioEnabled ? '#43a047' : '#e53935',
                                    }
                                }}
                            >
                                {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                            </IconButton>
                            <IconButton
                                onClick={switchCamera}
                                disabled={!videoDevices || videoDevices.length <= 1}
                                sx={{
                                    backgroundColor: '#2196f3',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#1976d2',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#424242',
                                        color: '#888',
                                    }
                                }}
                                title="Switch camera"
                            >
                                <CameraswitchIcon />
                            </IconButton>
                            <IconButton
                                onClick={() => setConversationDrawerOpen(!conversationDrawerOpen)}
                                sx={{
                                    backgroundColor: conversationDrawerOpen ? '#2196f3' : '#757575',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: conversationDrawerOpen ? '#1976d2' : '#616161',
                                    }
                                }}
                                title="Toggle conversation chat"
                            >
                                <MessageIcon />
                            </IconButton>
                            <IconButton
                                onClick={disconnectFromRoom}
                                sx={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#e53935',
                                    }
                                }}
                            >
                                <PhoneDisabledIcon />
                            </IconButton>
                        </Box>
                    )}

                    {/* Remote Video (shown during video call) */}
                    {videoRoom && isVideoCall && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 1 }}>
                                {isVideoExpanded ? 'Expanded Video View' : 'Remote Video'}
                            </Typography>

                            {/* Backdrop for expanded video */}
                            {isVideoExpanded && (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        width: '100vw',
                                        height: '100vh',
                                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                        zIndex: 1999,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onClick={toggleExpandedVideo}
                                />
                            )}

                            <Box
                                ref={remoteVideoRef}
                                sx={{
                                    width: isVideoExpanded ? '90vw' : '300px',
                                    height: isVideoExpanded ? '70vh' : '225px',
                                    backgroundColor: '#333',
                                    borderRadius: '8px',
                                    margin: '0 auto',
                                    border: '2px solid #2196f3',
                                    position: isVideoExpanded ? 'fixed' : 'relative',
                                    top: isVideoExpanded ? '10vh' : 'auto',
                                    left: isVideoExpanded ? '5vw' : 'auto',
                                    zIndex: isVideoExpanded ? 2000 : 'auto',
                                    transition: 'all 0.3s ease-in-out',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {remoteParticipants.size === 0 && (
                                    <Typography sx={{ color: '#888' }}>
                                        Waiting for participant to join...
                                    </Typography>
                                )}
                            </Box>

                            {/* Local video overlay in expanded mode */}
                            {isVideoExpanded && localVideoTrack && (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        top: '15vh',
                                        right: '7vw',
                                        width: '200px',
                                        height: '150px',
                                        zIndex: 2001,
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        border: '2px solid #2196f3',
                                        backgroundColor: '#333'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            '& video': {
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Expanded video controls */}
                            {isVideoExpanded && (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        bottom: '10vh',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        zIndex: 2002,
                                        display: 'flex',
                                        gap: 2,
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        borderRadius: '25px',
                                        p: 2
                                    }}
                                >
                                    <IconButton
                                        onClick={toggleVideo}
                                        sx={{
                                            backgroundColor: isVideoEnabled ? '#4caf50' : '#f44336',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: isVideoEnabled ? '#43a047' : '#e53935',
                                            }
                                        }}
                                    >
                                        {isVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                                    </IconButton>
                                    <IconButton
                                        onClick={toggleAudio}
                                        sx={{
                                            backgroundColor: isAudioEnabled ? '#4caf50' : '#f44336',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: isAudioEnabled ? '#43a047' : '#e53935',
                                            }
                                        }}
                                    >
                                        {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                                    </IconButton>
                                    <IconButton
                                        onClick={switchCamera}
                                        disabled={!videoDevices || videoDevices.length <= 1}
                                        sx={{
                                            backgroundColor: '#2196f3',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#1976d2',
                                            },
                                            '&:disabled': {
                                                backgroundColor: '#424242',
                                                color: '#888',
                                            }
                                        }}
                                    >
                                        <CameraswitchIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={toggleExpandedVideo}
                                        sx={{
                                            backgroundColor: '#9c27b0',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#7b1fa2',
                                            }
                                        }}
                                    >
                                        <CompressIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={disconnectFromRoom}
                                        sx={{
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#e53935',
                                            }
                                        }}
                                    >
                                        <PhoneDisabledIcon />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Dialpad */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        {dialpad.map((row, rowIdx) => (
                            <Box key={rowIdx} sx={{ display: 'flex', gap: 3, mb: rowIdx < 3 ? 2 : 0 }}>
                                {row.map((btn, colIdx) => (
                                    <Button
                                        key={btn.main}
                                        onClick={() => handleDialpadClick(btn.main === '0' && btn.sub === '+' ? (phoneNumber === '' ? '+' : '0') : btn.main)}
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            minWidth: 80,
                                            minHeight: 80,
                                            borderRadius: '50%',
                                            background: 'linear-gradient(145deg, #2196f3 60%, #64b5f6 100%)',
                                            color: '#111',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 0,
                                            m: 0,
                                            fontWeight: 500,
                                            fontSize: '2.1rem',
                                            transition: 'background 0.2s',
                                            border: 'none',
                                            '&:before': {
                                                content: '""',
                                                display: 'block',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '50%',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.10) 100%)',
                                                zIndex: 1
                                            },
                                            '&:hover': {
                                                background: 'linear-gradient(145deg, #1976d2 60%, #64b5f6 100%)',
                                                color: '#111'
                                            },
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                        disableElevation
                                    >
                                        <span style={{ fontSize: '2.1rem', fontWeight: 500, lineHeight: 1, zIndex: 2 }}>{btn.main}</span>
                                        {btn.sub && (
                                            <span style={{ fontSize: '0.95rem', fontWeight: 400, letterSpacing: '0.2em', marginTop: 2, zIndex: 2 }}>{btn.sub}</span>
                                        )}
                                    </Button>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* Call Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Button
                            onClick={handleCall}
                            disabled={!phoneNumber.trim() || calling || (isVideoCall && isAppleDevice() && !userInitiatedVideo)}
                            sx={{
                                width: 80,
                                height: 80,
                                minWidth: 80,
                                minHeight: 80,
                                borderRadius: '50%',
                                background: (!phoneNumber.trim() || calling || (isVideoCall && isAppleDevice() && !userInitiatedVideo))
                                    ? 'linear-gradient(145deg, #757575 60%, #9e9e9e 100%)'
                                    : 'linear-gradient(145deg, #4caf50 60%, #43e97b 100%)',
                                color: '#fff',
                                fontSize: '2.5rem',
                                boxShadow: '0 4px 16px rgba(76,175,80,0.18)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    background: (!phoneNumber.trim() || calling || (isVideoCall && isAppleDevice() && !userInitiatedVideo))
                                        ? 'linear-gradient(145deg, #757575 60%, #9e9e9e 100%)'
                                        : 'linear-gradient(145deg, #43e97b 60%, #4caf50 100%)',
                                    color: '#fff'
                                },
                                '&:disabled': {
                                    opacity: 0.6
                                }
                            }}
                            disableElevation
                        >
                            <CallIcon sx={{ fontSize: '2.5rem' }} />
                        </Button>
                    </Box>

                    {/* Helper text for Apple devices */}
                    {isVideoCall && isAppleDevice() && !userInitiatedVideo && (
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Typography variant="caption" sx={{ color: '#ff9800' }}>
                                Enable video above before making video calls on Safari
                            </Typography>
                        </Box>
                    )}

                    {/* Backspace Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <Tooltip title="Backspace">
                            <IconButton
                                onClick={handleBackspace}
                                disabled={!phoneNumber}
                                sx={{ color: '#888', fontSize: '2rem' }}
                            >
                                <BackspaceIcon sx={{ fontSize: '2rem' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Recent Numbers (placeholder for future enhancement) */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="textSecondary" align="center">
                            Recent numbers will appear here
                        </Typography>
                    </Box>
                </DialogContent>

                {/* Slide-out Conversation Panel */}
                <VideoCallSlideOutPanel
                    open={conversationDrawerOpen}
                    onClose={() => setConversationDrawerOpen(false)}
                    phoneNumber={phoneNumber}
                    videoRoom={videoRoom}
                    callStatus={callStatus}
                    onExpandVideo={toggleExpandedVideo}
                    isVideoExpanded={isVideoExpanded}
                />
            </Dialog>

            {/* Audio Settings Dialog */}
            <Dialog
                open={audioSettingsOpen}
                onClose={() => setAudioSettingsOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        padding: 2
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Media Settings
                    <IconButton onClick={() => setAudioSettingsOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Microphone (Input)
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel id="microphone-select-label">Select Microphone</InputLabel>
                            <Select
                                labelId="microphone-select-label"
                                value={selectedInputDevice || ''}
                                onChange={(e) => setSelectedInputDevice(e.target.value)}
                                label="Select Microphone"
                            >
                                {audioInputDevices.map((device) => (
                                    <MenuItem key={device.deviceId} value={device.deviceId}>
                                        {device.label || `Microphone (${device.deviceId.substring(0, 8)}...)`}
                                    </MenuItem>
                                ))}
                                {audioInputDevices.length === 0 && (
                                    <MenuItem value="" disabled>
                                        No microphones found
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => testAudioDevice('input')}
                            disabled={!selectedInputDevice}
                            sx={{ mt: 1 }}
                        >
                            Test Microphone
                        </Button>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Speaker (Output)
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel id="speaker-select-label">Select Speaker</InputLabel>
                            <Select
                                labelId="speaker-select-label"
                                value={selectedOutputDevice || ''}
                                onChange={(e) => setSelectedOutputDevice(e.target.value)}
                                label="Select Speaker"
                            >
                                {audioOutputDevices.map((device) => (
                                    <MenuItem key={device.deviceId} value={device.deviceId}>
                                        {device.label || `Speaker (${device.deviceId.substring(0, 8)}...)`}
                                    </MenuItem>
                                ))}
                                {audioOutputDevices.length === 0 && (
                                    <MenuItem value="" disabled>
                                        No speakers found
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => testAudioDevice('output')}
                            disabled={!selectedOutputDevice}
                            sx={{ mt: 1 }}
                        >
                            Test Speaker
                        </Button>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Camera (Video Input)
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel id="camera-select-label">Select Camera</InputLabel>
                            <Select
                                labelId="camera-select-label"
                                value={selectedVideoDevice || ''}
                                onChange={(e) => setSelectedVideoDevice(e.target.value)}
                                label="Select Camera"
                            >
                                {videoDevices.map((device) => (
                                    <MenuItem key={device.deviceId} value={device.deviceId}>
                                        {device.label || `Camera (${device.deviceId.substring(0, 8)}...)`}
                                    </MenuItem>
                                ))}
                                {videoDevices.length === 0 && (
                                    <MenuItem value="" disabled>
                                        No cameras found
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => testAudioDevice('video')}
                            disabled={!selectedVideoDevice}
                            sx={{ mt: 1 }}
                        >
                            Test Camera
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={getMediaDevices}
                            sx={{ mr: 1 }}
                        >
                            Refresh Devices
                        </Button>
                        <Button
                            variant="contained"
                            onClick={saveAudioSettings}
                            color="primary"
                        >
                            Save Settings
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Video Call Invitation Modal */}
            <Dialog
                open={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Video Call Invitation Sent</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                        You are now connected to the video call and ready to speak when the other person joins.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        An invitation has been sent to {phoneNumber}. You can also share this link:
                    </Typography>
                    <TextField
                        fullWidth
                        value={videoCallInviteUrl}
                        margin="normal"
                        variant="outlined"
                        label="Video Call Link"
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <IconButton
                                    onClick={() => {
                                        navigator.clipboard.writeText(videoCallInviteUrl);
                                        alert('Link copied to clipboard!');
                                    }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            ),
                        }}
                    />
                    <Typography variant="caption" color="textSecondary">
                        The other person will need to click this link to join your video call.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowInviteModal(false)}>Continue Call</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            navigator.clipboard.writeText(videoCallInviteUrl);
                            alert('Link copied to clipboard!');
                        }}
                    >
                        Copy Link
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FloatingDialer;